import { Icon, IconButton, Textarea, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { useGetCSEntries } from '@hero/services/content-stack-service/hooks';
import { generateFakeArray, getMathNumber } from '@hero/utils';

import * as S from './style';

const AI_STEP = {
  search: 'search',
  results: 'results'
};

const validationSchema = yup.object().shape({
  prompt: yup
    .string()
    .max(500)
    .required()
    .test('no-leading-space', 'Prompt should not start with a space', value => {
      return !value || !value.startsWith(' ');
    })
    .transform(value => value?.trimStart())
});

export function AIGiftAssistantTemplateForm({ setAiStep }) {
  const textAreaRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const { data: dataAIPromptSuggestions, isFetching: isFetchingAIPromptSuggestions } =
    useGetCSEntries({
      entryCode: 'sgb_dashboard_ai_prompt_suggestions'
    });
  const suggestions = dataAIPromptSuggestions?.entries?.[0]?.suggestions || [];

  const getRandomPrompts = () => {
    const shuffled = [...suggestions].sort(() => 0.5 - getMathNumber());
    return shuffled.slice(0, 6);
  };

  const [visiblePrompts, setVisiblePrompts] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const refreshPrompts = () => {
    setIsRefreshing(true);
    setVisiblePrompts(getRandomPrompts());
    // Reset refresh animation after animation completes
    setTimeout(() => setIsRefreshing(false), 1800); // 1.2s + 6*0.1s delay = 1.8s total
  };

  useEffect(() => {
    if (suggestions.length) {
      if (isInitialLoad) {
        // Initial load without animation
        setVisiblePrompts(getRandomPrompts());
        setIsInitialLoad(false);
      } else {
        refreshPrompts();
      }
    }
  }, [suggestions]);

  const [searchParams] = useSearchParams();
  const prompt = searchParams.get('prompt');

  const formik = useFormik({
    initialValues: {
      prompt: prompt || ''
    },
    validationSchema,
    onSubmit: values => {
      if (values.prompt) {
        setAiStep(AI_STEP.results);
        navigate(`/ai-gift-assistant?prompt=${values.prompt}`);
      }
    }
  });

  return (
    <S.AIGiftAssistantTemplateForm>
      <S.TemplateHeader>
        <Icon name="ai" size="xl" />
        <Typography variant="subtitle2Bold">{t('howCanIHelpYou')}</Typography>
        <S.TemplateHeaderBadge>
          <Typography variant="caption1SemiBold">{t('beta')}</Typography>
        </S.TemplateHeaderBadge>
      </S.TemplateHeader>

      <S.TemplateForm onSubmit={formik.handleSubmit}>
        <S.TextareaWrapper $animate={shouldAnimate}>
          <Textarea
            placeholder={t('tellMeAboutTheGiftAndYourBudget')}
            value={formik.values.prompt}
            onChange={e => {
              // Prevent leading spaces by trimming the input value
              const value = e.target.value;
              formik.setFieldValue('prompt', value.startsWith(' ') ? value.trimStart() : value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                formik.handleSubmit();
              }
            }}
            ref={textAreaRef}
          />
        </S.TextareaWrapper>

        <IconButton
          iconName="send"
          size="lg"
          style={{
            position: 'absolute',
            top: 16,
            right: 16
          }}
          type="submit"
          disabled={!formik.values.prompt || formik.values.prompt.trim() === ''}
        />
        <S.TemplateFormPrompts>
          <Typography variant="caption1">{t('tryAPrompt')}:</Typography>
          <S.TemplateFormPromptsList>
            {isFetchingAIPromptSuggestions ? (
              // Loading skeleton for prompts - 6 individual items
              <>
                {generateFakeArray(6, 'prompt').map(prompt => (
                  <ContentLoader
                    key={prompt}
                    speed={2}
                    width={getMathNumber(120, 220)}
                    height="24"
                    backgroundColor="#f3f3f3"
                    foregroundColor="#ecebeb"
                  >
                    <rect x="0" y="0" rx="4" ry="4" width="100%" height="24" />
                  </ContentLoader>
                ))}
              </>
            ) : (
              visiblePrompts.map((prompt, _index) => (
                <S.TemplateFormPromptsListItem
                  key={prompt}
                  variant="contained"
                  color="fg"
                  size="sm"
                  onClick={() => {
                    formik.setFieldValue('prompt', prompt);
                    setShouldAnimate(true);
                    textAreaRef.current.focus();
                    setTimeout(() => setShouldAnimate(false), 600);
                  }}
                  type="button"
                  style={{
                    '--hover-x': `${getMathNumber(0, 3)}px`,
                    '--hover-scale': `${getMathNumber(1.05, 1.08)}`,
                    '--hover-rotate': `${getMathNumber(0, 3)}deg`,
                    animation: isRefreshing
                      ? `dropAndSwing 1.2s ease-out ${_index * 0.1}s forwards`
                      : undefined
                  }}
                >
                  {prompt}
                </S.TemplateFormPromptsListItem>
              ))
            )}
          </S.TemplateFormPromptsList>

          <IconButton variant="text" style={{ marginLeft: 'auto' }}>
            <Icon name="refresh" size="sm" onClick={refreshPrompts} />
          </IconButton>
        </S.TemplateFormPrompts>
      </S.TemplateForm>
    </S.AIGiftAssistantTemplateForm>
  );
}
AIGiftAssistantTemplateForm.propTypes = {
  setAiStep: PropTypes.func.isRequired
};
